import {
  Brain,
  BookOpen,
  Scale,
  Lock,
  Lightbulb,
  TrendingUp,
  Zap,
  Target,
  Users,
  MessageSquare,
  Workflow,
  Sparkles,
  Rocket,
  Code,
  Palette,
  Shield,
  Database,
  GitBranch,
  Settings,
  Trophy,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * Pool de ícones para usar nos episódios
 * Cada ícone tem um significado visual que ajuda a identificar o tipo de conteúdo
 */
const iconPool: LucideIcon[] = [
  Brain, // 0 - Mentalidade/Conceitos
  BookOpen, // 1 - Teoria/Fundamentos
  Scale, // 2 - Ética/Balanço
  Lock, // 3 - Segurança/Privacidade
  Lightbulb, // 4 - Ideias/Inovação
  TrendingUp, // 5 - Crescimento/Progresso
  Zap, // 6 - Ação/Velocidade
  Target, // 7 - Objetivo/Foco
  Users, // 8 - Colaboração
  MessageSquare, // 9 - Comunicação
  Workflow, // 10 - Processos
  Sparkles, // 11 - IA/Automação
  Rocket, // 12 - Lançamento/Início
  Code, // 13 - Técnico/Código
  Palette, // 14 - Criatividade/Design
  Shield, // 15 - Proteção/Confiança
  Database, // 16 - Dados/Armazenamento
  GitBranch, // 17 - Ramificação/Decisões
  Settings, // 18 - Configuração/Ajustes
  Trophy, // 19 - Conquista/Sucesso
]

/**
 * Retorna ícone baseado no índice do módulo e número do episódio
 * Usa um hash simples para garantir consistência
 *
 * @param moduleIndex - Índice do módulo (0-based)
 * @param episodeNumber - Número do episódio (1-based)
 * @returns LucideIcon - Componente de ícone
 */
export function getIconForEpisode(moduleIndex: number, episodeNumber: number): LucideIcon {
  // Hash simples: (módulo * 6 + episódio - 1) % pool size
  // O -1 é porque episodeNumber começa em 1
  const hash = (moduleIndex * 6 + episodeNumber - 1) % iconPool.length
  return iconPool[hash]
}

/**
 * Opção alternativa: Mapear ícone por palavras-chave no título
 * Mais preciso mas requer manutenção
 *
 * @param title - Título do episódio
 * @returns LucideIcon - Componente de ícone
 */
export function getIconByTitle(title: string): LucideIcon {
  const lowerTitle = title.toLowerCase()

  // Palavras-chave para mentalidade/conceitos
  if (
    lowerTitle.includes('mentalidade') ||
    lowerTitle.includes('mindset') ||
    lowerTitle.includes('pensar')
  ) {
    return Brain
  }

  // Palavras-chave para teoria/fundamentos
  if (
    lowerTitle.includes('termos') ||
    lowerTitle.includes('básico') ||
    lowerTitle.includes('fundamento') ||
    lowerTitle.includes('conceito')
  ) {
    return BookOpen
  }

  // Palavras-chave para ética/limites
  if (
    lowerTitle.includes('ética') ||
    lowerTitle.includes('limite') ||
    lowerTitle.includes('responsável')
  ) {
    return Scale
  }

  // Palavras-chave para privacidade/segurança
  if (
    lowerTitle.includes('privacidade') ||
    lowerTitle.includes('segurança') ||
    lowerTitle.includes('proteção') ||
    lowerTitle.includes('dados')
  ) {
    return Lock
  }

  // Palavras-chave para ideias/inovação
  if (
    lowerTitle.includes('inteligência') ||
    lowerTitle.includes('aprimorar') ||
    lowerTitle.includes('melhorar') ||
    lowerTitle.includes('ideia')
  ) {
    return Lightbulb
  }

  // Palavras-chave para avaliação/métricas
  if (
    lowerTitle.includes('avaliar') ||
    lowerTitle.includes('medir') ||
    lowerTitle.includes('ajudando') ||
    lowerTitle.includes('resultado')
  ) {
    return TrendingUp
  }

  // Palavras-chave para comunicação
  if (
    lowerTitle.includes('prompt') ||
    lowerTitle.includes('comunicar') ||
    lowerTitle.includes('conversar') ||
    lowerTitle.includes('diálogo')
  ) {
    return MessageSquare
  }

  // Palavras-chave para processos/workflows
  if (
    lowerTitle.includes('processo') ||
    lowerTitle.includes('workflow') ||
    lowerTitle.includes('automatizar') ||
    lowerTitle.includes('fluxo')
  ) {
    return Workflow
  }

  // Palavras-chave para IA/tecnologia
  if (
    lowerTitle.includes('ia') ||
    lowerTitle.includes('artificial') ||
    lowerTitle.includes('modelo') ||
    lowerTitle.includes('algoritmo')
  ) {
    return Sparkles
  }

  // Palavras-chave para início/começar
  if (
    lowerTitle.includes('começar') ||
    lowerTitle.includes('iniciar') ||
    lowerTitle.includes('primeiro') ||
    lowerTitle.includes('introdução')
  ) {
    return Rocket
  }

  // Palavras-chave para técnico/código
  if (
    lowerTitle.includes('código') ||
    lowerTitle.includes('técnico') ||
    lowerTitle.includes('desenvolver') ||
    lowerTitle.includes('programar')
  ) {
    return Code
  }

  // Fallback: usa hash do comprimento do título
  const hash = title.length % iconPool.length
  return iconPool[hash]
}

/**
 * Retorna ícone híbrido: tenta por título, senão usa index
 * Melhor dos dois mundos
 */
export function getEpisodeIcon(
  title: string,
  moduleIndex: number,
  episodeNumber: number
): LucideIcon {
  // Tenta primeiro por título (mais semântico)
  const lowerTitle = title.toLowerCase()

  // Lista de palavras-chave prioritárias
  const keywords = [
    { words: ['mentalidade', 'mindset', 'pensar'], icon: Brain },
    { words: ['termos', 'básico', 'fundamento'], icon: BookOpen },
    { words: ['ética', 'limite', 'responsável'], icon: Scale },
    { words: ['privacidade', 'segurança', 'proteção'], icon: Lock },
    { words: ['inteligência', 'aprimorar', 'melhorar'], icon: Lightbulb },
    { words: ['avaliar', 'medir', 'ajudando'], icon: TrendingUp },
    { words: ['prompt', 'comunicar', 'conversar'], icon: MessageSquare },
    { words: ['processo', 'workflow', 'automatizar'], icon: Workflow },
    { words: ['ia', 'artificial', 'modelo'], icon: Sparkles },
    { words: ['código', 'técnico', 'desenvolver'], icon: Code },
  ]

  // Verifica se alguma palavra-chave está no título
  for (const { words, icon } of keywords) {
    if (words.some((word) => lowerTitle.includes(word))) {
      return icon
    }
  }

  // Fallback: usa índice
  return getIconForEpisode(moduleIndex, episodeNumber)
}
